import { useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { suspendStaffInputSchema, type StaffRow } from '@mybusinessmate/schema';
import { cx } from '../../lib/cx';
import { useSuspendStaff } from '../../lib/directory-api';
import { Icon } from '../Icon';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { useToast } from '../ui/toast';

type Person = Pick<StaffRow, 'id' | 'name' | 'activeTaskCount' | 'staffRole' | 'department'>;

const backOn = (days: number) =>
  new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(Date.now() + days * 86_400_000));

/** One of the three "How long" cards (Figma A04.1 "Suspend access"): a name, a line under it, a 1px or 1.5px stroke. */
function Duration({
  title,
  hint,
  selected = false,
  disabled = false,
}: {
  title: string;
  hint: string;
  selected?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      className={cx(
        'flex flex-col items-start gap-(--mbm-space-0-5) rounded-(--mbm-component-radius-option-tile) bg-surface-card px-(--mbm-space-3) py-(--mbm-space-2-5) text-left',
        'focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
        'disabled:cursor-not-allowed disabled:opacity-(--mbm-opacity-disabled)',
        selected ? 'border-[1.5px] border-action-primary' : 'border border-border-input',
      )}
    >
      <span className="text-label-medium text-text-primary">{title}</span>
      <span className="text-caption text-text-tertiary">{hint}</span>
    </button>
  );
}

/** A 36px outlined select, as the "open work" lines have them. */
function WorkSelect({ label, children, disabled = false }: { label: string; children: string; disabled?: boolean }) {
  return (
    <span
      className={cx(
        'relative flex h-(--mbm-size-button-small) w-(--mbm-layout-suspend-select-width) items-center rounded-(--mbm-component-radius-rail-item) border border-border-input bg-surface-card pl-(--mbm-space-3) pr-(--mbm-space-2-5)',
        disabled && 'opacity-(--mbm-opacity-disabled)',
      )}
    >
      <select
        aria-label={label}
        disabled={disabled}
        className="size-full min-w-0 flex-1 appearance-none bg-transparent pr-(--mbm-space-5) text-body-small text-text-primary outline-none"
      >
        <option>{children}</option>
      </select>
      <span aria-hidden className="pointer-events-none absolute right-(--mbm-space-2-5) text-text-primary">
        <Icon icon={CaretDown} size="xs" />
      </span>
    </span>
  );
}

function Form({ person, onClose }: { person: Person; onClose: () => void }) {
  const toast = useToast();
  const suspend = useSuspendStaff(person.id);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string>();
  const [formError, setFormError] = useState<string>();
  const leads = person.staffRole === 'lead_specialist';

  const submit = async () => {
    setFormError(undefined);
    const parsed = suspendStaffInputSchema.safeParse({ reason });
    if (!parsed.success) {
      setReasonError(parsed.error.issues[0]?.message);
      return;
    }
    setReasonError(undefined);
    try {
      const result = await suspend.mutateAsync({ reason: parsed.data.reason });
      const moved = result.tasksReturnedToIntake;
      toast({
        title: `${person.name} suspended`,
        description:
          moved > 0
            ? `${moved} ${moved === 1 ? 'task' : 'tasks'} went back to Task intake.`
            : 'They’re signed out and can’t sign in until you lift it.',
      });
      onClose();
    } catch (err) {
      const e = err as Partial<MappedApiError>;
      if (e.status === 409) setFormError(e.message ?? 'That isn’t allowed right now.');
      else if (e.kind === 'forbidden') setFormError('Only admins can suspend access.');
      else if (e.kind === 'not_found') setFormError('This person is no longer on the team.');
      else if (e.kind === 'unauthorized') setFormError('Your session expired. Please sign in again.');
      else setFormError('We couldn’t suspend access. Please try again.');
    }
  };

  return (
    <>
      {formError ? <Banner tone="error">{formError}</Banner> : null}
      {/* TODO(api): only "Until lifted" works. A timed lift needs a stored end date and a job to lift it, so the
          timed choices are drawn as designed and disabled. */}
      <div className="flex flex-col gap-(--mbm-space-2)">
        <span id="suspend-how-long" className="text-label-medium text-text-secondary">
          How long
        </span>
        <div role="radiogroup" aria-labelledby="suspend-how-long" className="grid grid-cols-3 gap-(--mbm-space-2)">
          <Duration title="7 days" hint={`Back on ${backOn(7)}`} disabled />
          <Duration title="30 days" hint={`Back on ${backOn(30)}`} disabled />
          <Duration title="Until lifted" hint="Blocked by an admin" selected />
        </div>
      </div>

      <div className="flex flex-col gap-(--mbm-space-2-5) rounded-(--mbm-component-radius-option-tile) bg-surface-canvas px-(--mbm-space-3-5) pb-(--mbm-space-3-5) pt-(--mbm-space-3)">
        <p className="text-label-small text-text-tertiary">Their open work</p>
        <div className="flex items-center justify-between gap-(--mbm-space-2-5)">
          <span className="text-body-small text-text-body">
            {person.activeTaskCount} open {person.activeTaskCount === 1 ? 'task' : 'tasks'}
          </span>
          {/* Tasks someone else is also on stay with them; the rest go back to intake. That is the only choice. */}
          <WorkSelect label="Open tasks">Back to Task intake</WorkSelect>
        </div>
        {leads ? (
          <div className="flex items-center justify-between gap-(--mbm-space-2-5)">
            <span className="text-body-small text-text-body">Lead of the {person.department ?? 'their'} pod</span>
            {/* TODO(api): handing a pod to someone else on suspension needs pods to be a thing of their own. */}
            <WorkSelect label="Pod lead" disabled>
              Pick a cover (coming soon)
            </WorkSelect>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-(--mbm-space-1-5)">
        <label htmlFor="suspend-reason" className="text-label-medium text-text-secondary">
          Reason (only admins see this)
        </label>
        <textarea
          id="suspend-reason"
          rows={2}
          value={reason}
          placeholder="Why is access being paused?"
          aria-invalid={reasonError ? true : undefined}
          onChange={(e) => setReason(e.target.value)}
          className={cx(
            'w-full resize-none rounded-(--mbm-radius-sm) border bg-surface-card px-(--mbm-space-3) py-(--mbm-space-2-5) text-body-small text-text-primary outline-none placeholder:text-text-muted',
            'focus:outline-2 focus:-outline-offset-2',
            reasonError
              ? 'border-feedback-error focus:outline-feedback-error'
              : 'border-border-input focus:outline-focus-ring',
          )}
        />
        {reasonError ? (
          <p role="alert" className="text-caption text-feedback-error">
            {reasonError}
          </p>
        ) : null}
      </div>

      <div className="flex justify-end gap-(--mbm-space-2) pt-(--mbm-space-1)">
        <Button
          variant="ghost"
          size="small"
          onClick={onClose}
          disabled={suspend.isPending}
          className="text-text-secondary"
        >
          Cancel
        </Button>
        <Button size="small" onClick={() => void submit()} loading={suspend.isPending}>
          Suspend access
        </Button>
      </div>
    </>
  );
}

/** Figma "Suspend access" (A04.1): a neutral confirmation with how long, what happens to their work, and a reason. */
export function SuspendStaffDialog({ person, onClose }: { person: Person | null; onClose: () => void }) {
  return (
    <Dialog
      open={!!person}
      onClose={onClose}
      size="wide"
      density="form"
      title={person ? `Suspend ${person.name}’s access?` : 'Suspend access?'}
      description="They’re signed out at once and can’t sign in. Their account, history and chats are kept, and you can lift this any time."
    >
      {person ? <Form key={person.id} person={person} onClose={onClose} /> : null}
    </Dialog>
  );
}
